import { v4 as uuid } from 'uuid';
import type {
  MediaFlow,
  MediaFlowKind,
  MediaStep,
  MediaStepEndOptions,
  MediaStepName,
  MediaStepOptions,
} from '../../model/MediaFlow';
import type { PlaybackStartupMilestone, PlaybackStartupObservation } from '../../model/PlaybackStartupObservation';

type ReportObservation = (observation: PlaybackStartupObservation) => void;

type ParsingOperation = Extract<
  MediaStepName,
  'playback-authorization' | 'license-authorization' | 'license' | 'manifest'
>;

const PARSING_OPERATIONS = new Set<ParsingOperation>([
  'playback-authorization',
  'license-authorization',
  'license',
  'manifest',
]);

function isParsingOperation(name: MediaStepName): name is ParsingOperation {
  return PARSING_OPERATIONS.has(name as ParsingOperation);
}

export class MediaFlowImpl implements MediaFlow {
  private closed = false;

  constructor(
    private readonly kind: MediaFlowKind,
    private readonly reportObservation: ReportObservation,
  ) {}

  startStep(name: MediaStepName, options: MediaStepOptions = {}): MediaStep {
    if (this.kind !== 'playback') {
      throw new Error(`Unsupported media flow kind: ${this.kind}`);
    }
    if (this.closed) {
      throw new Error('Cannot start a media step after the media flow has ended.');
    }

    const requestId = options.requestId ?? uuid();
    let terminal = false;
    let responseEnded = false;
    let parsingStarted = false;
    let parsingEnded = false;

    const report = (
      milestone: PlaybackStartupMilestone | 'parsing-start' | 'parsing-end',
      timestamp?: number,
      state?: MediaStepEndOptions['state'],
    ) => {
      if (terminal) return;
      const common = {
        timestamp,
        requestId,
        autoplayAllowed: options.autoplayAllowed,
        state,
      };
      if (milestone === 'parsing-start' || milestone === 'parsing-end') {
        if (isParsingOperation(name)) {
          this.reportObservation({ operation: name, milestone, ...common });
        }
        return;
      }
      this.reportObservation({ operation: name, milestone, ...common });
    };

    report('request-start');

    return {
      responseEnd: timestamp => {
        if (terminal || responseEnded) return;
        responseEnded = true;
        report('response-end', timestamp);
      },
      parsingStart: timestamp => {
        if (terminal || parsingStarted || !isParsingOperation(name)) return;
        parsingStarted = true;
        report('parsing-start', timestamp);
      },
      parsingEnd: timestamp => {
        if (terminal || parsingEnded || !isParsingOperation(name)) return;
        parsingEnded = true;
        report('parsing-end', timestamp);
      },
      end: options => {
        if (terminal) return;
        if (!responseEnded) {
          responseEnded = true;
          report('response-end', undefined, options?.state);
        }
        terminal = true;
      },
      fail: options => {
        if (terminal) return;
        report('response-error', undefined, options?.state);
        terminal = true;
      },
    };
  }

  end(): void {
    this.closed = true;
  }

  fail(): void {
    this.closed = true;
  }
}
